import { useState, useEffect } from "react";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Cards from "../../components/Cards";
import { FaFileInvoiceDollar, FaBoxOpen, FaClock, FaWallet } from "react-icons/fa";
import { TbSortAscending } from "react-icons/tb";
import { useAuth } from "../../utils/AuthContext";
import { Link } from "react-router-dom";
import { TiArrowRight } from "react-icons/ti";
import TopDoctorCard from "../../components/TopDoctorCard";
import PathologyPerformanceChart from "../../components/PathologyPerformanceChart";
import PathologyTestBreakdownChart from "../../components/PathologyTestBreakdownChart";
import TechnicalSupportNotice from "../../components/TechnicalSupportNotice";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const PathologistDashboard = () => {
  const [dashboardData, setDashboardData] = useState([]);
  const [doctorsData, setDoctorsData] = useState([]); 
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [billsLoading, setBillsLoading] = useState(true);
  const [pathologyTests, setPathologyTests] = useState([]);
  const [recentPathologyBills, setRecentPathologyBills] = useState([]);
  const [cashCard, setCashCard] = useState(null);

 useEffect(() => {
    const loadPathologistData = async () => {
      setLoading(true);
      try {
        const api = axios.create({ baseURL: BASE_URL, headers: { "x-api-key": API_KEY } });

        const today = new Date().toISOString().split("T")[0];
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;
        const monthPadded = String(month).padStart(2, '0');

        const [
          // stockRes, 
          doctorsRes,
          pathologyTestsRes,
          todayPathologyBillsRes,
          monthPathologyBillsRes,
          yearPathologyBillsRes,
          recentPathologyBillsRes
        ] = await Promise.all([
          api.get(`/doctor`).catch(() => ({ data: [] })),
          api.get(`/pathology`).catch(() => ({ data: [] })),
          api.get(`/pathology-bill/bills/range?startDate=${today}&endDate=${today}`).catch(() => ({ data: { totalTransactions: 0, totalAmount: 0 } })),
          api.get(`/pathology-bill/bills/monthly?year=${year}&month=${monthPadded}`).catch(() => ({ data: { bills: [], totalAmount: 0 } })),
          api.get(`/pathology-bill/bills/yearly?year=${year}`).catch(() => ({ data: { bills: [], totalAmount: 0 } })),
          api.get('/pathology-bill/?page=1&limit=5').catch(() => ({ data: { bills: [] } }))
        ]);
        setDoctorsData(doctorsRes.data?.data || doctorsRes.data || []);
        setBillsLoading(false);

        // --- PROCESS NEW DATA ---
        const pathologyTests = pathologyTestsRes.data || [];
        setPathologyTests(pathologyTests);
        const totalTests = pathologyTests.length;
        setRecentPathologyBills(recentPathologyBillsRes.data?.bills || []);
        setBillsLoading(false);
        
        // Calculate unique categories
        const categories = new Set(pathologyTests.map(test => test.categoryName));
        const totalCategories = categories.size;

        // Calculate total parameters
        const totalParameters = pathologyTests.reduce((sum, test) => sum + (test.parameters?.length || 0), 0);

        // --- Pathology Bills data ---
        const todayPathologyBillsCount = todayPathologyBillsRes.data?.totalTransactions || 0;
        const todayPathologyAmount = parseFloat(todayPathologyBillsRes.data?.totalAmount) || 0;
        const thisMonthPathologyBillsCount = (monthPathologyBillsRes.data?.data?.bills || monthPathologyBillsRes.data?.bills)?.length || 0;
        const thisMonthPathologyAmount = parseFloat(monthPathologyBillsRes.data?.data?.totalAmount || monthPathologyBillsRes.data?.totalAmount) || 0;
        const thisYearPathologyBillsCount = (yearPathologyBillsRes.data?.data?.bills || yearPathologyBillsRes.data?.bills)?.length || 0;
        const thisYearPathologyAmount = parseFloat(yearPathologyBillsRes.data?.data?.totalAmount || yearPathologyBillsRes.data?.totalAmount) || 0;

        // --- Turnaround Time ---
        const reportDaysArray = pathologyTests
        .map(test => test.reportDays)
        .filter(days => days != null && days > 0);

        let averageTurnaround = 0;
        if (reportDaysArray.length > 0) {
        const sumOfDays = reportDaysArray.reduce((sum, days) => sum + days, 0);
        averageTurnaround = (sumOfDays / reportDaysArray.length).toFixed(1);
        }

        const quickestTest = reportDaysArray.length > 0 ? Math.min(...reportDaysArray) : 0;
        const longestTest = reportDaysArray.length > 0 ? Math.max(...reportDaysArray) : 0;
        
        const cards = [
          {
            category: "Pathology Bills",
            icon: <FaFileInvoiceDollar className="text-purple-500 text-xl" />,
            total: todayPathologyBillsCount,
            status: "Today",
            cardListDetails: [
                { label: "Today's Amount", value: `৳${todayPathologyAmount.toLocaleString()}` },
                { label: "This Month", value: `${thisMonthPathologyBillsCount} bills (৳${thisMonthPathologyAmount.toLocaleString()})` },
                { label: "This Year", value: `${thisYearPathologyBillsCount} bills (৳${thisYearPathologyAmount.toLocaleString()})` },
            ],
            link: "/pathology-bill"
          },
          {
            category: "Available Lab Tests",
            icon: <FaBoxOpen className="text-blue-500 text-xl" />,
            total: totalTests,
            status: "Total Tests",
            cardListDetails: [
              { label: "Test Categories", value: totalCategories },
              { label: "Total Parameters", value: totalParameters },
            ],
            link: "/pathology-test"
          },
          {
            category: "Test Turnaround",
            icon: <FaClock className="text-orange-500 text-xl" />,
            total: `${averageTurnaround}`,
            status: "Avg. Days",
            cardListDetails: [
            { label: "Quickest Test", value: `${quickestTest} Day(s)` },
            { label: "Longest Test", value: `${longestTest} Day(s)` },
            ],
            link: "/pathology-test" 
        },
        ];
        
        setDashboardData(cards);

      } catch (error) {
        console.error("Error fetching pharmacist dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadPathologistData();
  }, []);

  // Load the logged-in pathologist's cash-in-hand (today / month / all-time)
  useEffect(() => {
    const name = user?.name;
    if (!name) return;
    const loadCash = async () => {
      try {
        const api = axios.create({ baseURL: BASE_URL, headers: { "x-api-key": API_KEY } });
        const today = new Date().toISOString().split("T")[0];
        const now = new Date();
        const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
        const uc = (start, end) =>
          api
            .get(`/pathology-accounts/user-collection?type=daily&startDate=${start}&endDate=${end}&userName=${encodeURIComponent(name)}`)
            .catch(() => ({ data: { userSummary: [] } }));

        const [todayRes, monthRes, allRes] = await Promise.all([
          uc(today, today),
          uc(monthStart, today),
          uc("2000-01-01", today),
        ]);

        const sumCash = (res) =>
          (res.data?.userSummary || []).reduce((s, u) => s + (Number(u.netCashInHand) || 0), 0);

        setCashCard({
          category: "My Cash in Hand",
          icon: <FaWallet className="text-green-600 text-xl" />,
          total: `৳${sumCash(todayRes).toLocaleString()}`,
          status: "Today",
          cardListDetails: [
            { label: "This Month", value: `৳${sumCash(monthRes).toLocaleString()}` },
            { label: "All Time", value: `৳${sumCash(allRes).toLocaleString()}` },
          ],
          link: "/pathology/user-collection",
        });
      } catch (e) {
        console.error("Error loading cash in hand:", e);
      }
    };
    loadCash();
  }, [user?.name]);

  // Load the logged-in pathologist's cash-in-hand (today / month / all-time)
  useEffect(() => {
    const name = user?.name;
    if (!name) return;
    const loadCash = async () => {
      try {
        const api = axios.create({ baseURL: BASE_URL, headers: { "x-api-key": API_KEY } });
        const today = new Date().toISOString().split("T")[0];
        const now = new Date();
        const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
        const uc = (start, end) =>
          api
            .get(`/pathology-accounts/user-collection?type=daily&startDate=${start}&endDate=${end}&userName=${encodeURIComponent(name)}`)
            .catch(() => ({ data: { userSummary: [] } }));

        const [todayRes, monthRes, allRes] = await Promise.all([
          uc(today, today),
          uc(monthStart, today),
          uc("2000-01-01", today),
        ]);

        const sumCash = (res) =>
          (res.data?.userSummary || []).reduce((s, u) => s + (Number(u.netCashInHand) || 0), 0);

        setCashCard({
          category: "My Cash in Hand",
          icon: <FaWallet className="text-green-600 text-xl" />,
          total: `৳${sumCash(todayRes).toLocaleString()}`,
          status: "Today",
          cardListDetails: [
            { label: "This Month", value: `৳${sumCash(monthRes).toLocaleString()}` },
            { label: "All Time", value: `৳${sumCash(allRes).toLocaleString()}` },
          ],
          link: "/pathology/user-collection",
        });
      } catch (e) {
        console.error("Error loading cash in hand:", e);
      }
    };
    loadCash();
  }, [user?.name]);

  return (
    <div className="md:py-4">
       {/* Top Title Area */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <h1 className="font-semibold text-lg">
          Welcome back, {user?.name || "Pathologist"} 👋
        </h1>
        <div>
          <Link to="/pathology-bill">
            <button className="py-2 px-4 bg-[#73CB64] hover:bg-green-700 text-white rounded-md flex items-center gap-2 text-sm">
              <TbSortAscending /> Add Bill
            </button>
          </Link>
        </div>
      </div>
      <div className="py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-3 gap-6">
          {loading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} height={150} />
            ))
          ) : (
            [...dashboardData, ...(cashCard ? [cashCard] : [])].map((card, i) => (
              <Cards key={i} data={card} isEven={i % 2 === 0} showLinkIcon={true} />
            ))
          )}
        </div>
      </div>
      {/* Main Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-6 xl:gap-y-0 xl:gap-6">
        <TechnicalSupportNotice />
        {/* Pathology Stats */}
        <div className="col-span-2 bg-white shadow rounded-lg">
          <div className="flex flex-wrap gap-3 justify-between p-4 border-b">
            <h2 className="font-medium">
              Monthly Performance (Sales vs Purchases)
            </h2>
            <ul className="flex gap-2">
              {["All", "1M", "6M", "1Y"].map((t) => (
                <li
                  key={t}
                  className="w-9 py-1 text-center text-sm bg-gray-100 hover:bg-gray-200 rounded-sm cursor-pointer"
                >
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-4">
                {/* Use the new chart component */}
            {loading ? <Skeleton height={400} /> : <PathologyPerformanceChart />}
        </div>
        </div>
      </div>
      {/* Top Doctors & Breakdown */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-6 xl:gap-y-0 xl:gap-6 mt-6">
        {/* Top Doctors */}
        <div className="col-span-2 bg-white shadow rounded-lg">
            <div className="p-4 border-b">
              <h2 className="font-medium">Top Doctors</h2>
            </div>
            <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-3  gap-4">
              {loading ? (
              Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} height={120} />)
            ) : doctorsData.length > 0 ? (
              [...doctorsData]
                .sort((a, b) => {
                  const patientsB = parseInt(b.translations.en.patientAttended) || 0;
                  const patientsA = parseInt(a.translations.en.patientAttended) || 0;
                  if (patientsB !== patientsA) {
                    return patientsB - patientsA;
                  }
                  const experienceB = parseInt(b.translations.en.yearsOfExperience) || 0;
                  const experienceA = parseInt(a.translations.en.yearsOfExperience) || 0;
                  return experienceB - experienceA;
                })
                .slice(0, 6)
                .map((doc) => (
                  <TopDoctorCard key={doc.id} doctor={doc} isAdmin={false} />
                ))
            ) : (
              <p className="col-span-3 text-center text-gray-500 py-4">No doctors available.</p>
            )}
            </div>
            <div className="p-4 border-t">
              <Link to="https://muktihospital.com/doctor">
                <button className="px-3 py-2 bg-[#73CB64] hover:bg-M-primary-color/90 transition-all duration-300 text-white rounded-md flex items-center gap-1 text-sm">
                  See All Doctors <TiArrowRight />
                </button>
              </Link>
            </div>
          </div>

        {/* Patient Breakdown */}
        <div className="bg-white shadow rounded-lg">
          <div className="flex flex-wrap gap-3 justify-between p-4 border-b">
            <h2 className="font-medium">Test Breakdown by Category</h2>
          </div>
          <div className="p-5">
            {loading ? (
              <Skeleton height={400} />
            ) : (
              <PathologyTestBreakdownChart tests={pathologyTests} isLoading={loading} />
            )}
          </div>
        </div>
      </div>
       {/* PHARMACY BILLS TABLE SECTION --- */}
    <div className="mt-6 bg-white shadow rounded-lg">
      <div className="flex justify-between items-center p-4 border-b">
        <h2 className="font-medium">Recent Pathology Bills</h2>
        <Link to="/pathology-bill">
          <button className="px-3 py-2 bg-[#73CB64] hover:bg-M-primary-color/90 transition-all duration-300 text-white rounded-md flex items-center gap-1 text-sm">
            View All <TiArrowRight />
          </button>
        </Link>
      </div>
      <div className="p-4 overflow-x-auto">
        {billsLoading ? (
          <Skeleton height={200} />
        ) : (
          <table className="min-w-full w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Bill No</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Net Amount (৳)</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Due Amount (৳)</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted By</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentPathologyBills.length > 0 ? (
                recentPathologyBills.map((bill) => (
                  <tr key={bill.id} className="hover:bg-gray-50">
                    <td className="p-3 text-sm text-gray-700">{bill.billNumber || '-'}</td>
                    <td className="p-3 text-sm text-gray-700">
                      {bill.createdAt ? new Date(bill.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}
                    </td>
                    <td className="p-3 text-sm text-gray-700">{bill.netAmount?.toFixed(2) || '0.00'}</td>
                    <td className="p-3 text-sm text-gray-700">{bill.dueAmount?.toFixed(2) || '0.00'}</td>
                    <td className="p-3 text-sm text-gray-700">{bill.userName || 'Unknown'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-gray-500">
                    No recent bills found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
    </div>
  );
};

export default PathologistDashboard;